import { useI18n, validateTunisianPhone } from '@ecom/i18n';
import { forwardRef, useId, useState, type ReactNode } from 'react';

export interface PhoneInputProps {
  readonly id?: string;
  readonly value: string;
  readonly onChange: (
    rawValue: string,
    result: { valid: boolean; normalized: string | undefined },
  ) => void;
  readonly 'aria-describedby'?: string;
  readonly 'aria-invalid'?: true;
  readonly required?: boolean;
  readonly disabled?: boolean;
}

/**
 * Tunisian phone number field (PDF §27): fixed `+216` prefix, digits-only
 * entry, live validation against `@ecom/i18n`'s `validateTunisianPhone` —
 * the same function the API will use server-side, so client and server
 * agreement on "is this a valid number" is not something that can drift.
 *
 * The prefix is a separate, non-editable segment rather than part of the
 * text the user types — this matches the prototype's checkout field
 * ("+216 22 345 678" as a single display, entered as local digits only) and
 * removes an entire class of input error (typing the country code wrong or
 * omitting it).
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { id, value, onChange, required, disabled, ...aria },
  ref,
): ReactNode {
  const { t } = useI18n();
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [touched, setTouched] = useState(false);

  // '+216' is the unambiguous prefix form in validateTunisianPhone (a
  // direct slice(4), not the length-dependent bare-'216' branch), so the
  // local 8 digits this field collects are validated against it explicitly.
  const result = validateTunisianPhone(value.length > 0 ? `+216${value}` : '');
  const showInvalid = touched && value.length > 0 && !result.valid;

  return (
    <div className="ecom-phone" data-invalid={showInvalid || undefined}>
      <span className="ecom-phone__prefix" aria-hidden="true">
        {t('phone.countryCode')}
      </span>
      <input
        ref={ref}
        id={inputId}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        className="ecom-phone__input"
        placeholder={t('phone.placeholder')}
        value={value}
        required={required}
        disabled={disabled}
        aria-invalid={showInvalid ? true : aria['aria-invalid']}
        aria-describedby={aria['aria-describedby']}
        onChange={(event) => {
          // Digits only: the field's whole purpose is to remove ambiguity
          // about what was typed, so non-digit characters are stripped
          // rather than merely rejected.
          const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 8);
          const validated = validateTunisianPhone(digitsOnly.length > 0 ? `+216${digitsOnly}` : '');
          onChange(digitsOnly, { valid: validated.valid, normalized: validated.normalized });
        }}
        onBlur={() => {
          setTouched(true);
        }}
      />
    </div>
  );
});
